// Model versions on the export history page (owner, 2026-09-17: model
// publishes are versioned here in model management, not filed in
// Document Control). One row per publish, numbered over the project's
// whole history, saying which Revit documents it changed since the
// version before it. The server fingerprints each version's documents
// from its own tiles in the background; a row still being fingerprinted
// says so and the list polls until it is done.
import React, { useEffect, useState } from "react";
import { Tooltip, Skeleton } from "antd";
import moment from "moment";
import { getModelVersions } from "api/modelVersions/modelVersionsApi";

const T = {
  ink: "#23343E", text: "#3A4952", muted: "#8D8D8D", line: "#EFEFEF",
  wash: "#E9F1F5", navyD: "#3A5765", added2: "#317A1B", removed2: "#C53030",
  amber: "#8A6112", ambBg: "#FDF4E1",
};
const POLL_MS = 10000;
const POLL_FOR_MS = 10 * 60 * 1000;
const SHOW = 8;

const who = (u) => (u && typeof u === "object" ? [u.name, u.lastName].filter(Boolean).join(" ") || u.email || "" : "");
const n = (x) => Number(x || 0).toLocaleString();

// The one-line answer to "what did this publish change?"
export function describeChanges(v) {
  const c = v.documentChanges;
  if (c === null || c === undefined) return { pending: true, text: "Working out which documents changed…" };
  if (c.unavailable) return { muted: true, text: "Older export format: which documents changed was not recorded" };
  if (c.first && c.baseline) return { text: `${v.documents ? v.documents.length : 0} documents · the versions before this one predate document tracking` };
  if (c.first) return { text: `First version · ${v.documents ? v.documents.length : 0} documents` };
  const parts = [];
  if (c.changed.length) parts.push(`${c.changed.length} changed`);
  if (c.added.length) parts.push(`${c.added.length} added`);
  if (c.removed.length) parts.push(`${c.removed.length} removed`);
  if (!parts.length) return { text: `No document changed · ${c.unchanged} unchanged` };
  return { text: `${parts.join(" · ")} · ${c.unchanged} unchanged` };
}

function Changes({ v }) {
  const c = v.documentChanges;
  if (!c || c.first || c.unavailable) return null;
  const chip = (key, color, label, title) => (
    <Tooltip key={key} title={title}>
      <span style={{ display: "inline-block", margin: "4px 6px 0 0", padding: "1px 7px", borderRadius: 10, fontSize: 11.5, background: "#F6F8F9", border: `1px solid ${T.line}`, color }}>
        {label}
      </span>
    </Tooltip>
  );
  return (
    <div>
      {c.changed.map((d) => chip(`c${d.name}`, T.navyD, d.name,
        d.kind === "geometry" ? `Same ${n(d.after)} elements; geometry or placement changed`
          : d.before === d.after ? `${n(d.after)} elements; some replaced by others` : `${n(d.before)} → ${n(d.after)} elements`))}
      {c.added.map((name) => chip(`a${name}`, T.added2, `+ ${name}`, "New in this version"))}
      {c.removed.map((name) => chip(`r${name}`, T.removed2, `− ${name}`, "Not in this version"))}
      {c.regenerated && (
        <div style={{ fontSize: 11.5, color: T.muted, marginTop: 4 }}>
          Exported with regenerated geometry, so files are compared by their elements only.
        </div>
      )}
    </div>
  );
}

export default function ModelVersions({ projectId }) {
  const [versions, setVersions] = useState(null);
  const [err, setErr] = useState(null);
  const [all, setAll] = useState(false);

  useEffect(() => {
    if (!projectId) return undefined;
    let cancelled = false;
    let timer = null;
    const started = Date.now();
    const load = async () => {
      try {
        const r = await getModelVersions(projectId);
        if (cancelled) return;
        const rows = (r && r.versions) || [];
        setVersions(rows);
        setErr(null);
        if (rows.some((v) => v.documentChanges == null) && Date.now() - started < POLL_FOR_MS) {
          timer = setTimeout(load, POLL_MS);
        }
      } catch (e) {
        if (!cancelled) setErr(e?.message || "Could not load model versions");
      }
    };
    load();
    return () => { cancelled = true; if (timer) clearTimeout(timer); };
  }, [projectId]);

  if (err) return null; // the rest of the page still works
  if (!versions) return <Skeleton active paragraph={{ rows: 2 }} style={{ marginBottom: 20 }} />;
  if (!versions.length) return null;
  const shown = all ? versions : versions.slice(0, SHOW);

  return (
    <div style={{ background: "#fff", border: `1px solid ${T.line}`, borderRadius: 8, padding: "14px 18px", marginBottom: 20 }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 10, marginBottom: 8 }}>
        <div style={{ fontSize: 15, fontWeight: 700, color: T.ink }}>Model versions</div>
        <div style={{ fontSize: 12, color: T.muted }}>
          {versions.length} publish{versions.length === 1 ? "" : "es"} · which Revit documents each one changed
        </div>
      </div>
      {shown.map((v) => {
        const d = describeChanges(v);
        return (
          <div key={v._id} data-testid="model-version-row" style={{ display: "grid", gridTemplateColumns: "64px minmax(0, 1fr)", gap: 12, padding: "9px 0", borderTop: `1px solid ${T.line}` }}>
            <div style={{ fontWeight: 700, color: T.navyD, fontSize: 15 }}>{v.number ? `v${v.number}` : "—"}</div>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 12.5, color: T.text, display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center" }}>
                <span>{moment(v.promotedAt || v.createdAt).format("MMM D, YYYY HH:mm")}</span>
                {who(v.userId) && <span style={{ color: T.muted }}>· {who(v.userId)}</span>}
                {v.status === "live" && <span style={{ padding: "0 7px", borderRadius: 10, background: T.wash, color: T.navyD, fontSize: 11, fontWeight: 600 }}>Current</span>}
                {v.status === "pending" && <span style={{ padding: "0 7px", borderRadius: 10, background: T.ambBg, color: T.amber, fontSize: 11 }}>Awaiting approval</span>}
                {v.status === "rejected" && <span style={{ padding: "0 7px", borderRadius: 10, background: "#FFF0F0", color: T.removed2, fontSize: 11 }}>Rejected</span>}
                <span style={{ color: T.muted, fontSize: 11 }}>{v.exportVersion}</span>
              </div>
              <div style={{ fontSize: 13, color: d.pending || d.muted ? T.muted : T.ink, marginTop: 2, fontStyle: d.pending ? "italic" : "normal" }}>{d.text}</div>
              <Changes v={v} />
            </div>
          </div>
        );
      })}
      {versions.length > SHOW && (
        <div style={{ paddingTop: 8, borderTop: `1px solid ${T.line}` }}>
          <a onClick={() => setAll((x) => !x)} style={{ fontSize: 12.5 }}>{all ? "Show fewer" : `Show all ${versions.length}`}</a>
        </div>
      )}
    </div>
  );
}
